import type { LotEvent } from '../types/lot'

// Validation day 1 follow-up: Historial listed "Actualización" cards with
// nothing under them. The change was real but lived in the lot's metadata
// (the egg arrival, Eclosión / Primera alimentación, the capacity table), which
// the card did not read. Each card now says what changed; a fish move between
// two units of the same lot (findings #183) says how many fish went where.

export type LotHistoryDetail =
  | { kind: 'stageChange'; from?: string; to: string; date?: string }
  | { kind: 'eggArrival'; eggs?: number; supplier?: string; tua?: number }
  | { kind: 'capacityTable' }
  | { kind: 'fishMove'; fishMoved: number; fromProductionUnitId?: string; toProductionUnitId?: string }
  | { kind: 'restored' }
  | { kind: 'hatcheryFields'; changes: HatcheryFieldChange[] }

/** Findings #195: one egg-arrival field edited after the arrival (UTA, diameter, certificates…). */
export type HatcheryFieldChange = { field: HatcheryHistoryField; from?: string | number | boolean | string[]; to?: string | number | boolean | string[] }

/** The egg-arrival fields Historial describes when they change (Ovas recibidas has its own line). */
export const HATCHERY_HISTORY_FIELDS = [
  'tuaAtArrival',
  'hatchTargetTua',
  'firstFeedingTargetTua',
  'eggDiameterMm',
  'vonBayerCount',
  'eggsPerGram',
  'eggsPerLitre',
  'eggVolumeLitres',
  'supplierLot',
  'supplierCertification',
  'broodstockGroup',
  'spawningDate',
  'stockTraits',
  'healthCertificateNo',
  'healthCertificateDate',
  'quarantine',
  'disinfectionProduct',
  'disinfectionPpm',
  'disinfectionMinutes',
  'disinfectionDate'
] as const
export type HatcheryHistoryField = typeof HATCHERY_HISTORY_FIELDS[number]

const record = (value: unknown): Record<string, unknown> | undefined => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined
)
const text = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value.trim() : undefined)
const num = (value: unknown): number | undefined => (typeof value === 'number' && Number.isFinite(value) ? value : undefined)

/** The lot an update / create event belongs to (a self-loop on one lot). */
const ownLotId = (event: LotEvent): string | undefined => (
  event.targetLotIds.length === 1 && (event.sourceLotIds.length === 0 || event.sourceLotIds[0] === event.targetLotIds[0])
    ? event.targetLotIds[0]
    : undefined
)

const fieldValue = (value: unknown): string | number | boolean | string[] | undefined => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (Array.isArray(value)) {
    const items = value.filter((item): item is string => typeof item === 'string' && item.trim() !== '')
    return items.length > 0 ? items : undefined
  }
  return text(value)
}

const lifeStageDate = (metadata: Record<string, unknown>, stage: string): string | undefined => {
  const history = Array.isArray(metadata.lifeStageHistory) ? metadata.lifeStageHistory : []
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const entry = record(history[index])
    if (entry && entry.stage === stage) return text(entry.date)
  }
  return undefined
}

const capacityId = (metadata: Record<string, unknown>): string | undefined => {
  const capacity = record(metadata.carryingCapacity)
  if (!capacity) return undefined
  return text(record(capacity.table)?.id) ?? JSON.stringify(capacity.table ?? capacity)
}

const describeChange = (event: LotEvent, previous: Record<string, unknown>): LotHistoryDetail[] => {
  const metadata = record(event.metadata) ?? {}
  const details: LotHistoryDetail[] = []

  const hatchery = record(metadata.hatchery)
  const previousHatchery = record(previous.hatchery)
  if (hatchery && num(hatchery.eggsReceived) !== undefined && num(hatchery.eggsReceived) !== num(previousHatchery?.eggsReceived)) {
    details.push({
      kind: 'eggArrival',
      eggs: num(hatchery.eggsReceived),
      supplier: text(metadata.seedSupplier) ?? text(hatchery.supplier),
      tua: num(hatchery.tuaAtArrival)
    })
  }

  // Findings #195: any other egg-arrival field edited after the arrival.
  if (hatchery && previousHatchery) {
    const changes: HatcheryFieldChange[] = []
    for (const field of HATCHERY_HISTORY_FIELDS) {
      const from = fieldValue(previousHatchery[field])
      const to = fieldValue(hatchery[field])
      if (JSON.stringify(from ?? null) !== JSON.stringify(to ?? null)) changes.push({ field, from, to })
    }
    if (changes.length > 0) details.push({ kind: 'hatcheryFields', changes })
  }

  const stage = text(metadata.lifeStage)
  const previousStage = text(previous.lifeStage)
  if (stage && stage !== previousStage) {
    details.push({ kind: 'stageChange', from: previousStage, to: stage, date: lifeStageDate(metadata, stage) })
  }

  const capacity = capacityId(metadata)
  if (capacity && capacity !== capacityId(previous)) {
    details.push({ kind: 'capacityTable' })
  }

  return details
}

/**
 * What each card should say beyond the before/after fields, by event id.
 * `events` is Historial's list, oldest first; each create / update is compared
 * with the previous create / update of the same lot.
 */
export const buildLotHistoryDetails = (events: readonly LotEvent[]): Map<string, LotHistoryDetail[]> => {
  const out = new Map<string, LotHistoryDetail[]>()
  const lastMetadataByLot = new Map<string, Record<string, unknown>>()

  for (const event of events) {
    const fishMove = record(record(event.metadata)?.fishMove)
    if (fishMove) {
      const fishMoved = num(fishMove.fishMoved)
      if (fishMoved !== undefined) {
        out.set(event.id, [{
          kind: 'fishMove',
          fishMoved,
          fromProductionUnitId: text(fishMove.fromProductionUnitId),
          toProductionUnitId: text(fishMove.toProductionUnitId)
        }])
      }
      continue
    }
    if (event.eventType !== 'create' && event.eventType !== 'update') continue
    const lotId = ownLotId(event)
    if (!lotId) continue
    const metadata = record(event.metadata) ?? {}
    // Findings #197: a deleted lot brought back (Lotes → Eliminados → Restaurar).
    if (event.eventType === 'update' && metadata.restored === true) {
      out.set(event.id, [{ kind: 'restored' }])
      continue
    }
    if (event.eventType === 'update') {
      const details = describeChange(event, lastMetadataByLot.get(lotId) ?? {})
      if (details.length > 0) out.set(event.id, details)
    }
    lastMetadataByLot.set(lotId, metadata)
  }

  return out
}
